import {debounce} from 'lodash';
import {useEffect, useMemo, useState} from 'react';

import {addToast} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {ClassroomModelV1, SubjectModelV2, TeacherModelV1} from '@albert-io/models';
import {getTeacherQuery} from 'client/Classrooms/Classroom/Teacher/Subjects/TeacherSubjectsList.react';
import {useUpdateClassroomSubjectAccessMutation} from 'gql/shared/ClassroomSubjectAccess.queries.generated';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import {normalizeError} from 'lib/normalizeError';

// Lifted from the legacy TeacherSubjectsList: the classroom + its subjects (with curriculum area for
// the domain color) and the license context the access toggle reads.
const getClassroomQuery = (classId: string) =>
  resource('classroom_v1')
    .mandarkEndpoint(['classrooms_v1', classId])
    .include('subjects_v2')
    .include('subjects_v2.curriculum_area_v1')
    .include('subjects_v2.guides_v1')
    .withMeta('subject_v2,classroom_v1')
    .customQuery({meta: {context: {classroom: {licensed_subjects: true}}}});

export interface ClassSubjects {
  isReady: boolean;
  hasNoSubjects: boolean;
  classroom: ClassroomModelV1 | null;
  /** Sorted by name and filtered by the Show-hidden toggle. */
  subjects: SubjectModelV2[];
  /** Licensed teacher at a licensed school — gates the per-card access controls (adaptive is always on). */
  canManageAccess: boolean;
  hasHiddenSubjects: boolean;
  showHidden: boolean;
  setShowHidden: (show: boolean) => void;
  openAccess: (subject: SubjectModelV2) => void;
  togglePartialCredit: (classroomId: string, subject: SubjectModelV2) => void;
  /** The subject pending a close-access confirmation, or null. */
  pendingClose: SubjectModelV2 | null;
  requestCloseAccess: (subject: SubjectModelV2) => void;
  confirmCloseAccess: () => void;
  cancelCloseAccess: () => void;
}

export const useClassSubjects = (classroomId: string): ClassSubjects => {
  const classroomQuery = useMemo(() => getClassroomQuery(classroomId), [classroomId]);
  // Match the legacy: refetch subject access on mount / class change rather than serving a stale cache.
  useEffect(() => {
    classroomQuery.invalidateInterest();
  }, [classroomQuery]);
  const {isReady, resources} = useMandarkQueries([getTeacherQuery(), classroomQuery]);
  const teacher = (resources[0] as TeacherModelV1) ?? null;
  const classroom = (resources[1] as ClassroomModelV1) ?? null;

  const [showHidden, setShowHidden] = useState(false);
  const [pendingClose, setPendingClose] = useState<SubjectModelV2 | null>(null);
  const [updateClassroomSubjectAccess] = useUpdateClassroomSubjectAccessMutation();

  // Guarded on isReady: the teacher's school relationship is an empty stub until the query resolves,
  // so getSchool().getId() would throw mid-load.
  const canManageAccess = useMemo(() => {
    if (!isReady || !teacher) {
      return false;
    }
    const schoolId = teacher.getSchool().getId();
    return teacher.getMeta().isLicensed() && teacher.getLicenseStatusForSchool(schoolId);
  }, [isReady, teacher]);

  const allSubjects = useMemo(
    () =>
      isReady && classroom
        ? classroom
            .getSubjects()
            .sortBy((subject) => subject.getName().toLowerCase())
            .toArray()
        : [],
    [isReady, classroom]
  );

  const hasHiddenSubjects = allSubjects.some((subject) => subject.isHidden());
  const subjects = showHidden ? allSubjects : allSubjects.filter((subject) => !subject.isHidden());

  const setAccess = async (subject: SubjectModelV2, restricted: boolean) => {
    try {
      const result = await updateClassroomSubjectAccess({
        variables: {
          classroomId,
          subjectId: subject.getId(),
          input: {restrictFreePractice: restricted}
        }
      });
      const errors = result.data?.updateClassroomSubjectAccess?.errors;
      if (errors && errors.length > 0) {
        throw new Error(errors[0]?.message ?? 'Failed to update classroom subject access');
      }
      addToast({
        color: 'positive',
        title: 'Success!',
        message: `Independent practice has been ${
          restricted ? 'disabled' : 'enabled'
        } for ${subject.getName()}.`
      });
      classroomQuery.invalidateInterest();
    } catch (error) {
      addToast({
        color: 'negative',
        title: 'Something went wrong',
        message: normalizeError(error).message
      });
    }
  };

  // Debounced like the legacy: the card flips its optimistic toggle on every change and calls this
  // immediately, so rapid toggling would otherwise fire redundant mutations off stale relationship meta.
  const togglePartialCredit = useMemo(
    () =>
      debounce(async (classId: string, subject: SubjectModelV2) => {
        const partialCreditEnabled =
          subject
            .get('relationshipMeta')
            .get('classroom')
            .get(classId)
            .get('partial_credit_enabled') ?? false;
        try {
          const result = await updateClassroomSubjectAccess({
            variables: {
              classroomId: classId,
              subjectId: subject.getId(),
              input: {partialCreditEnabled: !partialCreditEnabled}
            }
          });
          const errors = result.data?.updateClassroomSubjectAccess?.errors;
          if (errors && errors.length > 0) {
            throw new Error(errors[0]?.message ?? 'Failed to update partial credit setting');
          }
          addToast({
            color: 'positive',
            title: 'Success!',
            message: `Partial credit ${
              partialCreditEnabled ? 'disabled' : 'enabled'
            } for ${subject.getName()}.`
          });
          classroomQuery.invalidateInterest();
        } catch (error) {
          addToast({
            color: 'negative',
            title: 'Something went wrong',
            message: normalizeError(error).message
          });
        }
      }, 250),
    [updateClassroomSubjectAccess, classroomQuery]
  );

  return {
    isReady,
    hasNoSubjects: isReady && allSubjects.length === 0,
    classroom,
    subjects,
    canManageAccess,
    hasHiddenSubjects,
    showHidden,
    setShowHidden,
    openAccess: (subject) => setAccess(subject, false),
    togglePartialCredit,
    pendingClose,
    requestCloseAccess: (subject) => setPendingClose(subject),
    confirmCloseAccess: () => {
      if (pendingClose) {
        setAccess(pendingClose, true);
        setPendingClose(null);
      }
    },
    cancelCloseAccess: () => setPendingClose(null)
  };
};
